import { Card, Divider, Pagination, Tag, Table, Button, Modal, Select, Form, Tabs } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAegisReports, exportAegisReport, triggerAegisManualScan, retrieveAegisRecipients } from '../../methods/redux/reducers/compliance';
import remakeQueryString from '../../methods/utils/remake-query-string';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;
`;

const AegisReports = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { aegisReports, aegisRecipients } = useAppSelector((store) => store.compliance);
	const [searchParams, setSearchParams] = useSearchParams();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isExportModalVisible, setIsExportModalVisible] = useState(false);
	const [selectedReport, setSelectedReport] = useState<any>(null);
	const [exportLoading, setExportLoading] = useState(false);
	const [scanLoading, setScanLoading] = useState(false);
	const [form] = Form.useForm();

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveAegisReports(queryData));
		}

		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: '20',
				sort: 'desc',
			});
		}

		dispatch(retrieveAegisRecipients());
	}, [searchParams]);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const showReportDetails = (record: any) => {
		setSelectedReport(record);
		setIsModalVisible(true);
	};

	const handleExport = async (values: any) => {
		setExportLoading(true);
		const success = await exportAegisReport({ adminEmails: values.emails });
		setExportLoading(false);
		if (success) {
			setIsExportModalVisible(false);
			form.resetFields();
		}
	};

	const handleManualScan = async () => {
		setScanLoading(true);
		const success = await triggerAegisManualScan();
		setScanLoading(false);
		if (success) {
			const queryData = Object.fromEntries(searchParams.entries());
			dispatch(retrieveAegisReports(queryData));
		}
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: '_id',
			key: '_id',
			render: (id: string) => <small>{id}</small>,
		},
		{
			title: 'Report Type',
			dataIndex: 'scanType',
			key: 'scanType',
			render: (type: string) => (
				<Tag color={type === 'NPM_AUDIT' ? 'orange' : 'cyan'}>
					{type}
				</Tag>
			),
		},
		{
			title: 'Overall Status',
			dataIndex: 'overallStatus',
			key: 'overallStatus',
			render: (status: string) => {
				let color = 'green';
				if (status === 'CRITICAL') color = 'purple';
				if (status === 'HIGH') color = 'red';
				if (status === 'MODERATE') color = 'orange';
				return <Tag color={color}>{status}</Tag>;
			},
		},
		{
			title: 'Vulnerabilities',
			key: 'vulnerabilities',
			render: (record: any) => (
				<div>
					<Tag color="red">{record.criticalCount} Critical</Tag>
					<Tag color="volcano">{record.highCount} High</Tag>
					<Tag color="orange">{record.totalVulnerabilities} Total</Tag>
				</div>
			),
		},
		{
			title: 'Date Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (date: string) => moment(date).format('MMM DD, YYYY / hh:mm a'),
		},
		{
			title: 'Action',
			key: 'action',
			render: (record: any) => (
				<Button type="link" onClick={() => showReportDetails(record)}>
					View Details
				</Button>
			),
		},
	];

	return (
		<Container>
			<Card>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
						<Button 
							type="text" 
							icon={<ArrowLeftOutlined />} 
							onClick={() => navigate(-1)} 
							style={{ fontSize: '18px' }}
						/>
						<div>
							<h1>Aegis Security Reports</h1>
							<p>Historical security scan reports and vulnerability audits.</p>
						</div>
					</div>
					<div style={{ display: 'flex', gap: '1em' }}>
						<Button type="default" onClick={handleManualScan} loading={scanLoading}>
							Run Manual Scan
						</Button>
						<Button type="primary" onClick={() => setIsExportModalVisible(true)}>
							Export PDF Report
						</Button>
					</div>
				</div>
				<Divider />

				<SearchFilter
					title="Filter Reports"
					searchFilters={[
						{
							label: 'Report Type',
							name: 'reportType',
							htmlElement: 'select',
							selectOptions: [
								{ value: 'NPM_AUDIT', name: 'NPM Audit' },
								{ value: 'THREAT_SUMMARY', name: 'Threat Summary' },
							],
						},
						{
							label: 'Status',
							name: 'status',
							htmlElement: 'select',
							selectOptions: [
								{ value: 'SUCCESS', name: 'Success' },
								{ value: 'FAILED', name: 'Failed' },
							],
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<Divider />

				<Table
					columns={columns}
					dataSource={aegisReports.records}
					rowKey="_id"
					pagination={false}
					loading={!aegisReports.records && aegisReports?.totalRecords === 0}
				/>

				<Divider />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					current={aegisReports?.currentPage || 1}
					total={aegisReports?.totalRecords || 0}
					pageSize={aegisReports?.pageSize || 20}
					showSizeChanger
					onShowSizeChange={(_, size) => updateQueryString({ perPage: size })}
				/>
			</Card>

			<Modal
				title={`Report Details - ${selectedReport?.scanType}`}
				visible={isModalVisible}
				onOk={() => setIsModalVisible(false)}
				onCancel={() => setIsModalVisible(false)}
				width={900}
			>
				<div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', marginBottom: '1.5em' }}>
					<Card size="small" title="Overall Status" style={{ flex: 1, minWidth: '150px' }}>
						<Tag color={selectedReport?.overallStatus === 'CRITICAL' ? 'purple' : 'red'}>
							{selectedReport?.overallStatus}
						</Tag>
					</Card>
					<Card size="small" title="Critical" style={{ flex: 1, minWidth: '100px' }}>
						<strong style={{ color: '#722ed1' }}>{selectedReport?.criticalCount}</strong>
					</Card>
					<Card size="small" title="High" style={{ flex: 1, minWidth: '100px' }}>
						<strong style={{ color: '#cf1322' }}>{selectedReport?.highCount}</strong>
					</Card>
					<Card size="small" title="Moderate" style={{ flex: 1, minWidth: '100px' }}>
						<strong style={{ color: '#d46b08' }}>{selectedReport?.moderateCount}</strong>
					</Card>
					<Card size="small" title="Low" style={{ flex: 1, minWidth: '100px' }}>
						<strong style={{ color: '#389e0d' }}>{selectedReport?.lowCount}</strong>
					</Card>
					<Card size="small" title="Total" style={{ flex: 1, minWidth: '100px' }}>
						<strong>{selectedReport?.totalVulnerabilities}</strong>
					</Card>
				</div>

				{selectedReport?.recommendations && selectedReport.recommendations.length > 0 && (
					<div style={{ marginBottom: '1.5em' }}>
						<h3>Recommendations</h3>
						<ul style={{ maxHeight: '200px', overflowY: 'auto', paddingLeft: '1.5em', border: '1px solid #f0f0f0', padding: '10px', borderRadius: '4px' }}>
							{selectedReport.recommendations.map((rec: string, index: number) => (
								<li key={index} style={{ marginBottom: '5px' }}>{rec}</li>
							))}
						</ul>
					</div>
				)}

				<Tabs defaultActiveKey="1">
					<Tabs.TabPane tab="Summary Targets" key="1">
						<Table
							size="small"
							pagination={false}
							columns={[
								{ title: 'Target', dataIndex: 'target', key: 'target' },
								{ title: 'Vulnerabilities', dataIndex: 'totalVulnerabilities', key: 'totalVulnerabilities' },
								{ title: 'Error', dataIndex: 'error', key: 'error', render: (err: any) => err || 'None' }
							]}
							dataSource={selectedReport?.summary?.targets || []}
							rowKey="target"
						/>
					</Tabs.TabPane>
					<Tabs.TabPane tab="Raw Data" key="2">
						<pre style={{ maxHeight: '300px', overflow: 'auto', backgroundColor: '#f5f5f5', padding: '10px' }}>
							{JSON.stringify(selectedReport, null, 2)}
						</pre>
					</Tabs.TabPane>
				</Tabs>
			</Modal>

			<Modal
				title="Export Security Report"
				visible={isExportModalVisible}
				onCancel={() => setIsExportModalVisible(false)}
				footer={null}
			>
				<p>Generate a PDF security report and optionally email it to selected admins.</p>
				<Form form={form} onFinish={handleExport} layout="vertical">
					<Form.Item 
						name="emails" 
						label="Admin Emails (Optional)"
						tooltip="Type email and press Enter to add multiple"
					>
						<Select 
							mode="tags" 
							placeholder="Enter admin emails" 
							style={{ width: '100%' }}
						>
							{aegisRecipients?.records?.map((recipient: any) => {
								const email = typeof recipient === 'string' ? recipient : recipient.email;
								return <Select.Option key={email} value={email}>{email}</Select.Option>;
							})}
						</Select>
					</Form.Item>
					<Form.Item>
						<Button type="primary" htmlType="submit" loading={exportLoading} block>
							Generate & Send Report
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Container>
	);
};

export default AegisReports;
